import { useMemo } from 'react'

import {
  CreateRecordTypeMutation,
  CreateRecordTypeMutationVariables,
  GetAllFieldTemplatesInOrgQuery,
  GetAllFieldTemplatesInOrgQueryVariables,
  GetAllFieldTemplatesInRecordTypeQuery,
  GetAllFieldTemplatesInRecordTypeQueryVariables,
  GetRecordTypeSettingsQuery,
  GetRecordTypeSettingsQueryVariables,
  RecordTypeForRecordTemplatesQuery,
  RecordTypeForRecordTemplatesQueryVariables,
  RecordTypeOverviewQuery,
  RecordTypeOverviewQueryVariables,
  RecordTypeQuery,
  RecordTypeQueryVariables,
  RecordTypesOverviewQuery,
  RecordTypesOverviewQueryVariables,
  RecordTypesQuery,
  RecordTypesQueryVariables,
  RecordTypeStatusQuery,
  RecordTypeStatusQueryVariables,
  UpdateOverdueTaskDigestSubscribersMutation,
  UpdateOverdueTaskDigestSubscribersMutationVariables,
  UpdateRecordTypeMutation,
  UpdateRecordTypeMutationVariables,
} from 'types/graphql'

import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { useIsRenewalsOverhaulEnabled } from 'src/utils/featureFlagging/featureFlagging'
import { QueryKey, useMutation, useQuery, UseQueryOptions } from 'src/utils/queryClient'

const QueryRecordTypeStatus = gql`
  query RecordTypeStatusQuery($recordTypeId: Int!) {
    recordType: recordTypeById(id: $recordTypeId) {
      renewalsMigrationCompletedAt
      renewalsRollbackStartedAt
      statuses {
        ...RecordTypeStatusFragment
      }
    }
  }
`

/**
 * Returns true when the record type has completed renewals migration and has
 * not been rolled back. This mirrors the backend's migration-state check and
 * drives conditional hiding / filtering of status pickers across the UI.
 */
export const isRenewalsMigrated = (
  recordType:
    | {
        renewalsMigrationCompletedAt?: string | null
        renewalsRollbackStartedAt?: string | null
      }
    | null
    | undefined
): boolean =>
  recordType?.renewalsMigrationCompletedAt != null && recordType?.renewalsRollbackStartedAt == null

type RecordTypeRenewalsMigrationState = {
  renewalsMigrationStartedAt?: string | null
  renewalsRollbackStartedAt?: string | null
} | null

export type RecordTemplateRenewalPolicyAssignment = {
  archivedAt?: string | null
  renewalPolicy?: { id: number } | null
}

/**
 * True when at least one non-archived record template on the module has a
 * renewal policy assigned.
 */
export const hasAnyActiveRecordTemplateWithRenewalPolicy = (
  recordTemplates: RecordTemplateRenewalPolicyAssignment[] | null | undefined
): boolean =>
  (recordTemplates ?? []).some(
    (recordTemplate) =>
      recordTemplate.archivedAt == null && recordTemplate.renewalPolicy?.id != null
  )

type RecordTypeLegacyRenewalsUiState = {
  renewalsRollbackStartedAt?: string | null
  recordTemplates?: RecordTemplateRenewalPolicyAssignment[] | null
} | null

/**
 * Suppress legacy renewals configuration when the new system is in use for this
 * module: RenewalsOverhaul flag on, at least one active record template has a
 * renewal policy assigned, and rollback has not started. Migration state is not
 * required — attaching a policy is sufficient to lock legacy expiration and
 * renewal workflow settings.
 *
 * Aligns with the policy-attached portion of
 * `isRenewalsOverhaulBridgeActiveForRecord` in api/src/services/renewals/util.ts,
 * without the migration requirement (config UI locks as soon as staff attach a policy).
 */
export const isLegacyRenewalsUiSuppressed = (
  recordType: { renewalsRollbackStartedAt?: string | null } | undefined,
  isRenewalsOverhaulEnabled: boolean,
  hasAssignedRenewalPolicy: boolean
): boolean =>
  isRenewalsOverhaulEnabled &&
  recordType?.renewalsRollbackStartedAt == null &&
  hasAssignedRenewalPolicy

/** Hook wrapper for `isLegacyRenewalsUiSuppressed` with policy lookup from record templates. */
export const useIsLegacyRenewalsUiSuppressed = (
  recordType: RecordTypeLegacyRenewalsUiState | undefined
): boolean => {
  const isRenewalsOverhaulEnabled = useIsRenewalsOverhaulEnabled()
  const hasAssignedRenewalPolicy = useMemo(
    () => hasAnyActiveRecordTemplateWithRenewalPolicy(recordType?.recordTemplates),
    [recordType?.recordTemplates]
  )

  return useMemo(
    () =>
      isLegacyRenewalsUiSuppressed(
        recordType ?? undefined,
        isRenewalsOverhaulEnabled,
        hasAssignedRenewalPolicy
      ),
    [recordType, isRenewalsOverhaulEnabled, hasAssignedRenewalPolicy]
  )
}

/**
 * True when the renewals overhaul migration is active for this record type
 * (org flag on, migration started, no rollback). Does not consider policy
 * assignments — use `isLegacyRenewalsUiSuppressed` for configuration gating.
 * Mirrors `isRenewalsOverhaulEnabledForRecord` in
 * api/src/services/renewals/updateRenewalTrackerExpirationDate.ts.
 */
export const isRenewalsOverhaulActiveForRecordType = (
  recordType: RecordTypeRenewalsMigrationState | undefined,
  isRenewalsOverhaulEnabled: boolean
): boolean =>
  isRenewalsOverhaulEnabled &&
  recordType?.renewalsMigrationStartedAt != null &&
  recordType?.renewalsRollbackStartedAt == null

/** @deprecated Use `useIsLegacyRenewalsUiSuppressed` for legacy configuration UI gating. */
export const useIsRenewalsOverhaulActiveForRecordType = (
  recordType: RecordTypeRenewalsMigrationState | undefined
): boolean => {
  const isRenewalsOverhaulEnabled = useIsRenewalsOverhaulEnabled()

  return useMemo(
    () => isRenewalsOverhaulActiveForRecordType(recordType, isRenewalsOverhaulEnabled),
    [isRenewalsOverhaulEnabled, recordType]
  )
}

/**
 * After migration, workflow statuses remain in use for in-flight applications.
 * Hide legacy rows only when a renewal status shares the same display name.
 * Mirrors `filterRecordTypeStatusesForMigratedRecordType` in
 * api/src/services/views/view-templates/shared-columns/record-columns.ts.
 */
export const filterRecordTypeStatusesForMigratedRecordType = <
  T extends { name: string; renewalStatus?: string | null },
>(
  statuses: T[]
): T[] => {
  const renewalStatusNames = new Set(
    statuses.filter((status) => status.renewalStatus != null).map((status) => status.name)
  )
  return statuses.filter(
    (status) => status.renewalStatus != null || !renewalStatusNames.has(status.name)
  )
}

export const useRecordTypeStatusesQuery = (args: { recordTypeId: number | null | undefined }) => {
  return useQuery<RecordTypeStatusQuery, RecordTypeStatusQueryVariables>({
    queryKey: [QueryKey.RecordTypeStatuses, args.recordTypeId],
    queryDocument: QueryRecordTypeStatus,
    variables: {
      recordTypeId: args.recordTypeId ?? -1,
    },
    enabled: !!args.recordTypeId,
  })
}

const RecordTypes = gql`
  query RecordTypesQuery($organizationId: Int!) {
    recordTypesForOrg(organizationId: $organizationId) {
      id
      name
    }
  }
`
export const useRecordTypesQuery = (
  organizationId: number,
  options?: UseQueryOptions<RecordTypesQuery, RecordTypesQueryVariables>
) => {
  return useQuery<RecordTypesQuery, RecordTypesQueryVariables>({
    queryKey: [QueryKey.RecordTypes, organizationId],
    queryDocument: RecordTypes,
    variables: { organizationId },
    ...options,
  })
}

const RecordType = gql`
  query RecordTypeQuery($recordTypeId: Int!) {
    recordType: recordTypeById(id: $recordTypeId) {
      ...RecordTypeFragment
    }
  }
`
export const useRecordTypeQuery = (recordTypeId: number | null | undefined) => {
  return useQuery<RecordTypeQuery, RecordTypeQueryVariables>({
    queryKey: [QueryKey.RecordType, recordTypeId],
    queryDocument: RecordType,
    variables: {
      recordTypeId: recordTypeId ?? -1,
    },
    enabled: !!recordTypeId,
  })
}

const RecordTypeForRecordTemplates = gql`
  query RecordTypeForRecordTemplatesQuery($recordTypeId: Int!) {
    recordType: recordTypeById(id: $recordTypeId) {
      ...ModulePageRecordTypeFragment
    }
  }
  fragment ModulePageRecordTypeFragment on RecordType {
    id
    recordTemplates {
      ...RecordTemplateForConfigFragment
    }
    archivedRecordTemplates {
      ...ArchivedRecordTemplateForConfigFragment
    }
    category
  }
  fragment RecordTemplateForConfigFragment on RecordTemplate {
    id
    recordTypeId
    name
    tags
    applicantType
    uuid
    prefix
    visibleOnModulePickPage
    visibleOnOrganizationHomepage
    owner {
      ...UserDataFragment
    }
    ownerUserFormula {
      ...FormulaOptionFragment
    }
    createdByUser {
      ...UserDataFragment
    }
    applicationGroup {
      id
      name
    }
    # Used by Renewals tab to detect record templates that have a renewal
    # policy but no designated renewal workflow
    defaultWorkflowTemplateFormula {
      id
    }
    workflowTemplates {
      id
      isRenewal
    }
  }
  fragment ArchivedRecordTemplateForConfigFragment on RecordTemplate {
    archivedAt
    archivedByUser {
      ...UserDataFragment
    }
    id
    name
    prefix
    uuid
  }
`

export const useRecordTypeForConfigQuery = (recordTypeId: number) => {
  return useQuery<RecordTypeForRecordTemplatesQuery, RecordTypeForRecordTemplatesQueryVariables>({
    queryDocument: RecordTypeForRecordTemplates,
    queryKey: [QueryKey.RecordTypeForRecordTemplates, recordTypeId],
    variables: { recordTypeId },
  })
}

const RecordTypesOverview = gql`
  query RecordTypesOverviewQuery($organizationId: Int!) {
    recordTypes(organizationId: $organizationId) {
      ...RecordTypeOverviewFragment
    }
  }
  fragment RecordTypeOverviewFragment on RecordType {
    id
    icon
    name
    recordName
    displayName
    category
    shouldEmailCitizens
    shouldEmailStaff
    shouldShowInStaffNavigation
    visibleOnOrganizationHomepage
    areMeetingsEnabled
  }
`

export const useRecordTypesOverviewQuery = () => {
  const organizationId = useMyOrgId()
  const { data, ...rest } = useQuery<RecordTypesOverviewQuery, RecordTypesOverviewQueryVariables>({
    queryDocument: RecordTypesOverview,
    queryKey: [QueryKey.RecordTypesOverview, organizationId],
    variables: { organizationId: organizationId ?? -1 },
    enabled: !!organizationId,
  })
  const recordTypes = useMemo(() => data?.recordTypes || [], [data])
  return {
    ...rest,
    recordTypes,
  }
}

const Create = gql`
  mutation CreateRecordTypeMutation($input: CreateRecordTypeInput!) {
    createRecordType(input: $input) {
      id
    }
  }
`
export const useCreateRecordTypeMutation = () => {
  return useMutation<CreateRecordTypeMutation, CreateRecordTypeMutationVariables>({
    mutationDocument: Create,
  })
}

const Update = gql`
  mutation UpdateRecordTypeMutation($id: Int!, $input: UpdateRecordTypeInput!) {
    updateRecordType(id: $id, input: $input) {
      id
    }
  }
`

export const useUpdateRecordTypeMutation = () => {
  return useMutation<UpdateRecordTypeMutation, UpdateRecordTypeMutationVariables>({
    mutationDocument: Update,
  })
}

const GetAllFieldTemplatesInRecordType = gql`
  query GetAllFieldTemplatesInRecordTypeQuery($recordTypeId: Int!) {
    recordType: recordTypeById(id: $recordTypeId) {
      recordTemplates {
        id
        name
        applicationFields {
          ...FieldTemplateListFragment
        }
      }
    }
  }
`
export const useAllFieldTemplatesInRecordTypeQuery = (recordTypeId?: number | null) => {
  return useQuery<
    GetAllFieldTemplatesInRecordTypeQuery,
    GetAllFieldTemplatesInRecordTypeQueryVariables
  >({
    queryKey: [QueryKey.RecordTypeFields, recordTypeId],
    queryDocument: GetAllFieldTemplatesInRecordType,
    variables: {
      recordTypeId: recordTypeId ?? -1,
    },
    enabled: !!recordTypeId,
  })
}

const GetAllFieldTemplatesInOrg = gql`
  query GetAllFieldTemplatesInOrgQuery($organizationId: Int) {
    recordTypesUserHasAccessTo(organizationId: $organizationId) {
      id
      name
      recordTemplates {
        id
        name
        applicationFields {
          ...FieldTemplateListFragment
        }
      }
    }
  }
`
export const useAllFieldTemplatesInOrgQuery = (
  options?: UseQueryOptions<GetAllFieldTemplatesInOrgQuery, GetAllFieldTemplatesInOrgQueryVariables>
) => {
  const organizationId = useMyOrgId()
  return useQuery<GetAllFieldTemplatesInOrgQuery, GetAllFieldTemplatesInOrgQueryVariables>({
    queryKey: [QueryKey.AllFieldTemplatesInOrg, organizationId],
    queryDocument: GetAllFieldTemplatesInOrg,
    variables: { organizationId },
    enabled: !!organizationId && (options?.enabled ?? true),
    ...options,
  })
}

const RecordTypeOverview = gql`
  query RecordTypeOverviewQuery($recordTypeId: Int!, $organizationId: Int!) {
    recordTypeById(id: $recordTypeId) {
      ...RecordTypeForModulePageFragment
    }
    availableFormulas: getFormulas(input: { organizationId: $organizationId }) {
      ...FormulaOptionFragment
    }
  }
  fragment RecordTypeForModulePageFragment on RecordType {
    id
    displayName
    icon
    addressEntryType
    automationTemplates {
      ...AutomationTemplateFragment
    }
    issuedIdentifierAlgorithm
    moduleType
    renewalsMigrationStartedAt
    renewalsRollbackStartedAt
    visibleOnOrganizationHomepage
    connectedFormulas {
      ...FormulaOptionFragment
    }
    recordSettings {
      ...RecordSettingsFragment
    }
    recordTemplatesWithExpirationAutomationTemplate {
      id
      name
      uuid
    }
    recordTemplates {
      id
      archivedAt
      renewalPolicy {
        id
      }
    }
    allowInspections
    allowFees
    allowPlanReviews
    applyDescription
    areMeetingsEnabled
    displayName
    icon
    icon
    isContact
    name
    recordName
    shouldEmailCitizens
    shouldEmailStaff
    shouldShowInStaffNavigation
    overdueTaskDigestSubscribers {
      id
      user {
        ...UserDataFragment
      }
    }
    isUsingOrganizationDefaultLetterhead
    organization {
      id
      slug
    }
  }
`

export const useRecordTypeOverviewQuery = (recordTypeId: number) => {
  const organizationId = useMyOrgId()
  const { data, ...rest } = useQuery<RecordTypeOverviewQuery, RecordTypeOverviewQueryVariables>({
    queryDocument: RecordTypeOverview,
    queryKey: [QueryKey.RecordTypeOverview, recordTypeId],
    variables: { recordTypeId, organizationId: organizationId ?? -1 },
    enabled: !!organizationId,
  })
  const availableFormulas = useMemo(() => data?.availableFormulas || [], [data])
  return {
    availableFormulas,
    recordType: data?.recordTypeById,
    ...rest,
  }
}

const GetRecordTypeSettings = gql`
  query GetRecordTypeSettingsQuery($recordTypeId: Int!) {
    recordTypeById(id: $recordTypeId) {
      id
      recordSettingsDerived {
        ...OverridableRecordSettingsFragment
      }
      recordSettingsDerivedSources {
        ...OverridableRecordSettingSourcesFragment
      }
    }
  }
`

export const useRecordTypeSettingsQuery = (
  recordTypeId: number,
  options?: UseQueryOptions<GetRecordTypeSettingsQuery, GetRecordTypeSettingsQueryVariables>
) => {
  const { data, ...rest } = useQuery<
    GetRecordTypeSettingsQuery,
    GetRecordTypeSettingsQueryVariables
  >({
    queryKey: [QueryKey.RecordTypeSettings, recordTypeId],
    queryDocument: GetRecordTypeSettings,
    variables: {
      recordTypeId,
    },
    ...options,
  })

  const recordSettings = useMemo(
    () => data?.recordTypeById?.recordSettingsDerived ?? {},
    [data?.recordTypeById?.recordSettingsDerived]
  )
  const recordSettingsSources = useMemo(
    () => data?.recordTypeById?.recordSettingsDerivedSources ?? {},
    [data?.recordTypeById?.recordSettingsDerivedSources]
  )

  return {
    recordSettings,
    recordSettingsSources,
    ...rest,
  }
}

const UpdateOverdueTaskDigestSubscribers = gql`
  mutation UpdateOverdueTaskDigestSubscribersMutation(
    $input: UpdateOverdueTaskDigestSubscribersInput!
  ) {
    updateOverdueTaskDigestSubscribers(input: $input) {
      id
    }
  }
`

export const useUpdateOverdueTaskDigestSubscribersMutation = () => {
  return useMutation<
    UpdateOverdueTaskDigestSubscribersMutation,
    UpdateOverdueTaskDigestSubscribersMutationVariables
  >({ mutationDocument: UpdateOverdueTaskDigestSubscribers })
}
